<template>
  <div class="flex flex-col bg-white w-full">

    <!-- Try New View Banner -->
    <div v-if="showNewViewBanner" class="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
      <div class="flex items-center">
        <i class="fa-solid fa-sparkles mr-2"></i>
        <span class="text-sm font-medium">A new enterprise Jobs view is available with project sidebar, maps, and SMS inbox.</span>
      </div>
      <div class="flex items-center space-x-3 ml-4 flex-shrink-0">
        <button @click="tryNewView()" class="bg-white text-indigo-600 px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-50">Try New View</button>
        <button @click="dismissNewViewBanner()" class="text-indigo-200 hover:text-white"><i class="fa-solid fa-xmark"></i></button>
      </div>
    </div>

    <div
      v-if="showNewClient"
      class="w-full h-full bg-gray-900 bg-opacity-20 flex items-center justify-center fixed z-50"
    >
      <div class="w-96 bg-white shadow-lg p-14 rounded-xl relative">
        <i
          @click="(showNewClient = false), (client = clients[0])"
          class="fa-solid fa-xmark cursor-pointer hover:text-red-700 absolute top-0 right-0 m-4"
        ></i>

        <h2 class="header-bold text-lg">Add New Client</h2>
        <input
          type="text"
          v-model="newClient"
          placeholder="Name of client"
          class="rounded h-10 my-2 bg-white w-full border border-gray-200 outline-none px-2 text-gray-600"
        />

        <div
          @click="saveClient()"
          class="bg-primary cursor-pointer hover:bg-secondary text-white rounded p-4 header-heavy"
        >
          Save Client
        </div>
      </div>
    </div>

    <!-- SMS Inbox Modal -->
    <div
      v-if="smsModal"
      class="w-full h-full bg-gray-900 bg-opacity-40 flex items-center justify-center fixed z-50 inset-0"
    >
      <div class="w-full max-w-2xl bg-white shadow-lg rounded-xl relative flex flex-col" style="max-height: 90vh;">
        <i
          @click="smsModal = false"
          class="fa-solid fa-xmark cursor-pointer hover:text-red-700 absolute top-0 right-0 m-4 z-10"
        ></i>

        <div class="p-6 pb-3 flex-shrink-0">
          <h2 class="header-bold text-lg mb-4">
            <i class="fa-solid fa-comment-sms text-indigo-600 mr-2"></i>SMS Inbox
          </h2>

          <!-- Filter: All Communication / Per Job -->
          <div class="flex items-center space-x-2 mb-3">
            <button
              @click="smsFilter = 'all'"
              :class="smsFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
            >All Communication</button>
            <button
              @click="smsFilter = 'job'"
              :class="smsFilter === 'job' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
            >By Job</button>
          </div>

          <!-- Job selector (visible when filtering by job) -->
          <div v-if="smsFilter === 'job'" class="mb-3">
            <select
              v-model="smsSelectedJobId"
              @change="onSmsJobFilterChange()"
              class="rounded h-10 bg-gray-100 w-full outline-none px-3 text-gray-600 text-sm"
            >
              <option value="">Select a job...</option>
              <option v-for="j in smsJobOptions" :key="j.id" :value="j.id">
                {{ j.label }}
              </option>
            </select>
          </div>

        </div>

        <!-- Message history (scrollable, iMessage style) -->
        <div class="flex-1 overflow-y-auto flex flex-col-reverse" style="background: #f2f2f7;">
          <div v-if="smsHistoryLoading" class="flex items-center justify-center py-8">
            <i class="fa-solid fa-spinner fa-spin text-gray-400 text-xl mr-2"></i>
            <span class="text-sm text-gray-400">Loading messages...</span>
          </div>

          <div v-else-if="smsHistory.length > 0" class="px-4 py-3 space-y-1">
            <div
              v-for="(msg, idx) in smsHistory"
              :key="idx"
              class="flex"
              :class="msg.direction === 'inbound' ? 'justify-start' : 'justify-end'"
            >
              <div class="max-w-xs sm:max-w-sm">
                <div
                  class="imsg-bubble px-3 py-2 text-sm inline-block"
                  :class="msg.direction === 'inbound' ? 'imsg-bubble-recv' : 'imsg-bubble-sent'"
                >
                  <div>{{ msg.body || msg.message }}</div>
                  <span v-if="msg.classification"
                    class="inline-block mt-1 text-xs px-1.5 py-0.5 rounded-full font-medium"
                    :class="{
                      'bg-green-400 bg-opacity-30 text-green-100': msg.classification === 'FIRM' && msg.direction !== 'inbound',
                      'bg-green-100 text-green-700': msg.classification === 'FIRM' && msg.direction === 'inbound',
                      'bg-yellow-400 bg-opacity-30 text-yellow-100': msg.classification === 'CHANGED' && msg.direction !== 'inbound',
                      'bg-yellow-100 text-yellow-700': msg.classification === 'CHANGED' && msg.direction === 'inbound',
                      'bg-red-400 bg-opacity-30 text-red-100': msg.classification === 'CANCELLED' && msg.direction !== 'inbound',
                      'bg-red-100 text-red-700': msg.classification === 'CANCELLED' && msg.direction === 'inbound',
                    }"
                  >{{ msg.classification }}</span>
                </div>
                <div class="flex items-center mt-0.5 px-1 space-x-2"
                  :class="msg.direction === 'inbound' ? 'justify-start' : 'justify-end'"
                >
                  <span class="text-xs text-gray-400">{{ formatSmsTimestamp(msg.timestamp || msg.created) }}</span>
                  <span v-if="msg._jobLabel" class="text-xs text-gray-400 truncate max-w-28">
                    {{ msg._jobLabel }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-sm text-gray-400 text-center py-8">
            <i class="fa-solid fa-inbox text-3xl text-gray-300 mb-2 block"></i>
            {{ smsFilter === 'job' && !smsSelectedJobId ? 'Select a job to view messages.' : 'No messages found.' }}
          </div>
        </div>

        <!-- Compose bar (iMessage style, bottom) -->
        <div class="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3 rounded-b-xl">
          <div class="flex items-center mb-2">
            <label class="text-xs text-gray-400 mr-2">To:</label>
            <select v-model="smsPhone" class="text-sm text-gray-700 bg-transparent outline-none flex-1">
              <option v-if="phone" :value="normalizePhoneForSms(phone)">{{ contact || 'Primary' }} — {{ phone }}</option>
              <option v-if="secondaryPhone" :value="normalizePhoneForSms(secondaryPhone)">{{ secondaryContact || 'Secondary' }} — {{ secondaryPhone }}</option>
            </select>
          </div>
          <div v-if="visibleSmsTemplates.length > 0" class="flex items-center mb-2">
            <label class="text-xs text-gray-400 mr-2">Template:</label>
            <select v-model="selectedSmsTemplateId" @change="applySmsTemplate()"
              class="text-sm text-gray-700 bg-transparent outline-none flex-1 cursor-pointer">
              <option value="">Custom message</option>
              <option v-for="t in visibleSmsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div class="flex items-end space-x-2">
            <div class="flex-1 rounded-full bg-gray-100 border border-gray-200 px-4 py-2">
              <textarea
                v-model="smsMessage"
                rows="1"
                placeholder="iMessage"
                class="w-full bg-transparent text-sm text-gray-700 outline-none resize-none leading-5"
                style="max-height: 80px;"
                @input="$event.target.style.height = 'auto'; $event.target.style.height = $event.target.scrollHeight + 'px';"
              ></textarea>
            </div>
            <button
              @click="sendJobSms()"
              :disabled="smsLoading || !smsPhone || !smsMessage"
              :class="smsLoading || !smsPhone || !smsMessage ? 'opacity-30' : ''"
              class="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0 mb-0.5"
            >
              <i v-if="smsLoading" class="fa-solid fa-spinner fa-spin text-xs"></i>
              <i v-else class="fa-solid fa-arrow-up text-xs font-bold"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Client Link Modal -->
    <div
      v-if="linkModal"
      class="w-full h-full bg-gray-900 bg-opacity-40 flex items-center justify-center fixed z-50 inset-0"
    >
      <div class="w-full max-w-lg bg-white shadow-lg p-6 rounded-xl relative">
        <i
          @click="linkModal = false"
          class="fa-solid fa-xmark cursor-pointer hover:text-red-700 absolute top-0 right-0 m-4"
        ></i>

        <h2 class="header-bold text-lg mb-4">
          <i class="fa-solid fa-link text-green-600 mr-2"></i>Client Job Link
        </h2>

        <div v-if="linkJob" class="mb-3 text-sm text-gray-500">
          <span class="font-medium text-gray-700">{{ linkJob.company }}</span>
          <span v-if="linkJob.formattedAddress"> — {{ linkJob.formattedAddress }}</span>
        </div>

        <div class="flex items-center mb-4">
          <input
            type="text"
            :value="linkUrl"
            readonly
            class="rounded-l h-10 bg-gray-100 flex-1 outline-none px-3 text-gray-600 text-sm border border-r-0 border-gray-200"
            @click="$event.target.select()"
          />
          <button
            @click="copyLink"
            class="h-10 px-4 rounded-r text-sm font-medium flex items-center"
            :class="linkCopied ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-800'"
          >
            <i :class="linkCopied ? 'fa-solid fa-check' : 'fa-regular fa-copy'" class="mr-1"></i>
            {{ linkCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
      </div>
    </div>

    <nav-menu />
    <div class="h-auto w-full bg-gray-900 relative">
      <div
        v-if="setReminder"
        class="fixed z-10 inset-0 bg-gray-900 bg-opacity-25 overflow-y-auto"
      >
        <div class="flex items-center justify-center min-h-screen px-4">
          <div
            class="bg-white overflow-y-scroll relative rounded-lg p-6 shadow-lg w-96 h-96"
          >
            <i
              @click="setReminder = false"
              class="fa-solid fa-xmark cursor-pointer hover:text-red-700 absolute top-0 right-0 m-4"
            ></i>

            <h2 class="text-lg font-semibold mb-4">Reminder Calls</h2>

            <flat-pickr
              class="text-xs w-full bg-gray-100 h-12 rounded px-3 mb-2"
              :config="config"
              v-model="reminderDate"
            ></flat-pickr>

            <textarea
              name="reminderText"
              v-model="reminderText"
              id="reminderText"
              placeholder="reminder information"
              class="rounded bg-gray-100 p-2 text-xs text-gray-600 w-full h-12"
            ></textarea>
            <div
              @click="saveReminder()"
              class="rounded w-full h-12 flex items-center justify-center bg-primary text-white cursor-pointer hover:bg-secondary"
            >
              Set Reminder
            </div>
            <table class="w-full mt-4 text-xs">
              <thead>
                <tr class="bg-gray-200 h-10 text-gray-500 rounded-t py-2">
                  <th class="font-normal text-sm">Reminders</th>
                  <th class="font-normal text-sm"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  class="border-b border-gray-100 py-2 h-10"
                  v-for="(reminder, index) in reminders"
                  :key="index"
                >
                  <td class="text-left">
                    <div class="w-full">
                      {{
                        $moment(reminder.reminderDate.seconds * 1000).format(
                          "DD MMM YYYY - HH:mmA"
                        )
                      }}
                      <div class="text-xs w-full">
                        {{ reminder.reminderText }}
                      </div>
                    </div>
                  </td>

                  <td>
                    <button
                      @click="deleteReminder(reminder.id)"
                      class="text-red-500"
                    >
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="container mx-auto text-left flex items-center">
        <div class="flex-grow sm:ml-0 ml-6">
          <h2
            v-if="!editMode"
            class="text-white header-heavy tracking-tight text-base sm:text-xl mt-2"
          >
            Add New Job
          </h2>
          <h2
            v-else
            class="text-white header-heavy tracking-tight text-base sm:text-xl mt-2"
          >
            Edit Job
          </h2>
        </div>
        <div class="flex-shrink flex space-x-2">
          <div
            v-if="newProject"
            @click="submitForm(false)"
            class="bg-primary cursor-pointer hover:bg-secondary text-white rounded m-2 p-4 header-heavy"
          >
            Create Job
          </div>
          <div
            v-if="!newProject"
            @click="openMapModal()"
            class="bg-gray-700 flex items-center cursor-pointer hover:bg-gray-900 text-white rounded m-2 p-4 header-heavy"
          >
            <i class="fa-solid fa-map-location-dot"></i>
            <span class="sm:block hidden mx-1">Map</span>
          </div>
          <div
            v-if="!newProject"
            @click="openSmsInbox()"
            class="bg-indigo-600 flex items-center cursor-pointer hover:bg-indigo-800 text-white rounded m-2 p-4 header-heavy"
          >
            <i class="fa-solid fa-comment-sms"></i>
            <span class="sm:block hidden mx-1">SMS</span>
          </div>
          <div
            v-if="!newProject"
            @click="setReminder = true"
            class="bg-yellow-500 flex items-center cursor-pointer hover:bg-yellow-600 text-white rounded m-2 p-4 header-heavy"
          >
            <i class="hidden sm:block fa-solid fa-user-clock"></i>
            <span class="sm:block hidden mx-1">Set </span> Reminder
          </div>
          <div
            v-if="!newProject"
            @click="submitForm(true)"
            class="sm:mr-0 mr-4 flex bg-primary cursor-pointer hover:bg-secondary text-white rounded my-2 p-4 header-heavy"
          >
            Save <span class="sm:block mx-1 hidden">Job</span>
          </div>
        </div>
      </div>
    </div>

    <div
      class="h-auto w-full overflow-y-scroll relative rounded-t-xl bg-gray-200"
    >
      <transition name="slide">
        <div class="fixed top-0 right-0 z-50 sm:w-96" v-if="createCustomer">
          <create-customer :customer="customer" />
        </div>
      </transition>
      <div class="container mx-auto p-2">
        <div class="grid grid-cols-12 sm:gap-20">
          <div
            class="col-span-12 sm:col-span-3 flex flex-wrap items-center justify-start"
          >
            <div
              class="hidden sm:block flex-shrink mr-3 text-left text-sm mb-1"
            ></div>
            <select
              type="text"
              v-model="client"
              @change="openNewClient()"
              class="rounded flex-grow h-10 bg-white outline-none px-2 text-gray-600"
            >
              <option :value="c" v-for="(c, index) in clients" :key="index">
                {{ c }}
              </option>
              <option value="new">Add New Client</option>
            </select>
          </div>
          <div class="col-span-12 sm:col-span-8 h-full flex">
            <div class="w-2/3 text-left flex items-center">
              <div
                class="hidden sm:block flex-shrink mr-3 text-left text-sm mb-1"
              >
                <div class="w-14">Customer</div>
              </div>
              <div class="flex-grow relative">
                <multiselect
                  label="company"
                  v-if="!customer"
                  track-by="company"
                  @search-change="searchCustomers"
                  :loading="loadingSelect"
                  v-model="customer"
                  :options="customers"
                  :optionsLimit="300000"
                >
                </multiselect>

                <div
                  class="flex-shrink flex bg-gray-100 rounded px-2 py-2 text-sm w-96"
                  v-else
                >
                  <div class="flex w-full h-full items-center">
                    <span class="font-bold text-sm mr-2">{{
                      customer.company
                    }}</span>
                    -
                    <span class="ml-2 text-xs text-gray-400">{{
                      customer.contact
                    }}</span>
                    <!-- <div class="w-full flex text-xs">
                        <div class="flex-shrink font-bold">Contact Name: </div>
                        <div class="flex-shrink ml-2">{{ customer.contact }} </div>
                      </div>
                      <div class="w-full flex text-xs">
                        <div class="flex-shrink text-sm font-bold">Contact Phone: </div>
                        <div class="flex-shrink ml-2"> <a :href="'tel:' + customer.phone" class="text-primary cursor-pointer">{{ customer.phone }}</a> </div>
                      </div>
                      <div class="w-full flex text-xs">
                        <div class="flex-shrink font-bold">Contact Email: </div>
                        <div class="flex-shrink ml-2"> <a :href="'mailto:' + customer.email" class="text-primary cursor-pointer">{{ customer.email }}</a> </div>
                      </div> -->

                    <div class="flex-shrink ml-auto">
                      <span
                        @click="createCustomer = !createCustomer"
                        class="text-xs text-white rounded-full py-1 px-3 mx-2 bg-primary cursor-pointer hover:bg-secondary cursor-pointer"
                      >
                        Edit
                      </span>
                    </div>
                  </div>

                  <div
                    class="hidden sm:flex flex-shrink pt-1"
                    v-if="customer"
                    @click="customer = null"
                  >
                    <i
                      class="text-red-500 cursor-pointer fa-solid fa-xmark"
                    ></i>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!customer" class="w-1/3 pl-4 flex items-center">
              <div
                @click="createCustomer = !createCustomer"
                class="rounded w-full bg-gray-900 cursor-pointer text-white h-12 px-4 items-center justify-center flex"
              >
                New
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full bg-gray-300" v-if="customer">
        <div class="container mx-auto p-2">
          <div class="grid grid-cols-12 gap-2">
            <div
              v-if="!newProject"
              class="col-span-12 sm:col-span-4 h-full items-center flex"
            >
              <div
                class="flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">Project</div>
              </div>
              <div class="w-full relative">

                <DropdownSearch v-model="projectId" :options="projects" />
                
                <!-- 

               <select
                  type="text"
                  v-model="projectId"
                  placeholder="Project Name"
                  class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
                >
                  <option
                    :value="p.projectId"
                    v-for="(p, index) in projects"
                    :key="'pId' + index"
                  >
                    #{{ p.projectId }} - {{ p.projectName }}
                  </option>
                </select>  -->
              </div>
              <div class="flex-shrink">
                <div
                  @click="
                    showProject()
                  "
                  class="cursor-pointer hover:bg-secondary ml-4 h-10 w-10 bg-primary text-white rounded flex items-center justify-center"
                >
                  <i class="fa-solid fa-plus"></i>
                </div>
              </div>
            </div>

            <div
              v-if="newProject"
              class="col-span-12 sm:col-span-3 h-full items-center justify-end flex"
            >
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">Project ID</div>
              </div>
              <input
                type="number"
                :disabled="true"
                v-model="projectId"
                :placeholder="latestJob.projectId + 1"
                class="rounded h-10 bg-gray-200 w-full outline-none px-2 text-gray-400"
              />
            </div>

            <div
              v-if="newProject"
              class="col-span-12 sm:col-span-4 h-full items-center justify-end flex"
            >
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">Project Name</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="The Pavillion"
                v-model="projectName"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />

              <div class="flex-shrink">
                <div
                  @click="
                    newProject = false;
                    projectId = projects[0].projectId;
                  "
                  class="cursor-pointer hover:bg-secondary ml-4 h-10 w-10 bg-primary text-white rounded flex items-center justify-center"
                >
                  <i class="fa-solid fa-magnifying-glass"></i>
                </div>
              </div>
            </div>

            <div
              class="pl-2 pr-1 rounded-lg col-span-12 sm:col-span-4 sm:col-start-9 h-full items-center justify-end flex"
            >
              <!-- <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                Active
              </div>
              <select
                v-model="active"
                @change="autoSave()"
                placeholder="active"
                class="rounded h-8 bg-white w-20 outline-none px-2 text-gray-600"
              >
                <option :value="true">Active</option>
                <option :value="false">Inactive</option>
              </select> -->
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mx-3 text-left text-sm mb-1"
              >
                Call Frequency
              </div>
              <select
                @change="autoSave()"
                v-model="callFrequency"
                class="rounded w-40 h-8 bg-white outline-none px-2 text-gray-600"
              >
                <option value="daily">Daily</option>
                <option value="never">Don't Call</option>
                <option value="friday">Fridays</option>
              </select>
            </div>

            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">PO Number</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="PO"
                v-model="poNumber"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">Contact</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="Frank Junior"
                v-model="contact"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-14 text-right">Phone</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="+61431000000"
                v-model="phone"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-14 text-right">Email</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="frank@test.com"
                v-model="email"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">Contact 2</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="Secondary contact"
                v-model="secondaryContact"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-14 text-right">Phone 2</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="+61431000000"
                v-model="secondaryPhone"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div class="col-span-12 sm:col-span-3 h-12 items-center flex">
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-14 text-right">Email 2</div>
              </div>
              <input
                type="text"
                @keyup="autoSave()"
                placeholder="secondary@test.com"
                v-model="secondaryEmail"
                class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
              />
            </div>
            <div
              class="sm:col-start-1 col-span-12 sm:col-span-5 h-10 items-center flex"
            >
              <div
                class="sm:block hidden flex-shrink whitespace-nowrap mr-3 text-left text-sm mb-1"
              >
                <div class="w-20 text-right">Address</div>
                <!-- {{ address }} -->
              </div>
              <vue-google-autocomplete
                :options="autocompleteOptions"
                id="map"
                v-model="address"
                country="au"
                ref="autocomplete"
                v-on:inputChange="onInput"
                classname="rounded h-10 bg-white w-full outline-none px-2 text-gray-600"
                placeholder="Project Address"
                v-on:placechanged="getAddressData"
              >
              </vue-google-autocomplete>
            </div>
            <div
              class="pl-4 col-span-12 sm:col-span-3 h-10 items-center flex"
            >
            <div class="flex whitespace-nowrap text-sm mr-4 ">Region</div>
            <input type="text" v-model="region" class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600">
            </div>
            <div
              class="pl-4 col-span-12 sm:col-span-3 h-10 items-center flex"
            >
            <div class="flex whitespace-nowrap text-sm mr-4 ">Project Type</div>
            <input type="text" v-model="projectType" class="rounded h-10 bg-white w-full outline-none px-2 text-gray-600">
            </div>
            <div
              class="col-start-1 col-span-12 sm:col-span-3 h-10 items-center flex"
            >

            <div class="flex whitespace-nowrap text-sm">Estimated Start</div>
            <flat-pickr
                    class=" cursor-pointer w-40 ml-4 rounded h-10 bg-white outline-none px-2 text-gray-600"
                    :config="configDate"
                    v-model="estimatedStartDate"
                  ></flat-pickr>
                
                </div>
            <div
              class=" col-span-12 sm:col-span-3 h-10 items-center flex"
            >
            <div class="flex whitespace-nowrap text-sm">Estimated End</div>
            <flat-pickr
                    class="cursor-pointer w-40 ml-4 rounded h-10 bg-white outline-none px-2 text-gray-600"
                    :config="configDate"
                    v-model="estimatedEndDate"
                  ></flat-pickr>
                
                </div>
          </div>
        </div>
      </div>

      <div class="w-full bg-white">
        <div class="container mx-auto p-4">
          <div class="grid grid-cols-4 gap-4">
            <div
              class="col-span-4 sm:col-span-1"
              :class="j.status == 'COMPLETED' || j.status == 'CANCELLED' ? 'hidden' : ''"
              v-for="(j, index) in jobs"
              :key="index"
            >
              <div
                :class="[
                  editMode == j.id ? 'pulse-glow' : '',
                  j.status == 'COMPLETED'
                    ? 'bggreenstripes bg-green-600'
                    : ' bg-gray-100',
                ]"
                class="w-full rounded p-4 shadow relative"
              >
                <div
                  class="absolute top-0 right-0 -m-1"
                  v-if="index >= 1 || j.id"
                  @click="removeJob(index)"
                >
                  <i
                    class="fa-solid fa-circle-minus text-red-600 hover:text-red-700 cursor-pointer text-lg"
                  ></i>
                </div>
                <div class="w-full relative mb-1">
                  <div class="absolute top-0 right-0 mr-4 mt-5">
                    <i class="fa-solid fa-calendar-days"></i>
                  </div>

                  <flat-pickr
                    class="h-14 cursor-pointer w-full text-xs text-gray-600 text-center bg-gray-200"
                    :config="config"
                    v-model="j.date"
                  ></flat-pickr>

                  <!-- <datetime
                    v-model="j.date"
                    type="datetime"
                    placeholder="Date and time"
                    format="DD-MM-YYYY HH:mm"
                    input-class=" w-full rounded bg-gray-200 pr-6 outline-none pl-6 h-12 text-gray-500 text-sm"
                  ></datetime> -->
                </div>

                <div
                  v-if="j.testerNotes"
                  class="my-6 text-left text-xs w-full bg-red-100 text-red-600 rounded-xl p-4"
                >
                  <strong>Tester Notes:</strong><br />
                  {{ j.testerNotes }}
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Job Contact
                  </div>
                  <input
                    type="text"
                    v-model="j.jobContact"
                    placeholder="Job-specific contact"
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Job Phone
                  </div>
                  <input
                    type="text"
                    v-model="j.jobPhone"
                    placeholder="Job-specific mobile"
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Job Email
                  </div>
                  <input
                    type="text"
                    v-model="j.jobEmail"
                    placeholder="Job-specific email"
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Mix Code
                  </div>
                  <input
                    type="text"
                    v-model="j.mixCode"
                    placeholder="ie: 123"
                    class="rounded h-12 text-right w-full bg-gray-50 outline-none px-6 text-gray-600"
                  />
                </div>
                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Meters
                  </div>
                  <input
                    type="text"
                    v-model="j.meters"
                    placeholder="ie: 4"
                    class="rounded h-12 text-right w-full bg-gray-50 outline-none px-6 text-gray-600"
                  />
                </div>
                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Duration (min)
                  </div>
                  <input
                    type="number"
                    v-model="j.duration"
                    placeholder="ie: 60"
                    class="rounded h-12 text-right w-full bg-gray-50 outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full flex items-center justify-between mb-1 px-2 h-10">
                  <span class="text-xs text-gray-500">Flexible</span>
                  <div @click="j.flexible = !j.flexible"
                    :class="j.flexible ? 'bg-green-500' : 'bg-gray-300'"
                    class="relative w-10 h-5 rounded-full cursor-pointer transition-colors">
                    <div :class="j.flexible ? 'translate-x-5' : 'translate-x-0'"
                      class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"></div>
                  </div>
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Requirements
                  </div>
                  <input
                    type="text"
                    v-model="j.requirements"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Pour Location
                  </div>
                  <input
                    type="text"
                    v-model="j.pourLocation"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Notes for tester
                  </div>
                  <input
                    type="text"
                    v-model="j.notesForTester"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Notes
                  </div>
                  <input
                    type="text"
                    v-model="j.notes"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Tester
                  </div>
                  <select
                    type="text"
                    v-model="j.contractor"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  >
                    <option
                      v-for="(c, cIndex) in contractors"
                      :key="'con' + cIndex"
                      :value="c"
                    >
                      {{ c.name }}
                    </option>
                  </select>
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-white"
                  >
                    Status
                  </div>
                  <select
                    v-model="j.status"
                    :class="[
                      j.status == 'ON-CALL' ? 'bg-yellow-600 text-white' : '',
                      j.status == 'FIRM'
                        ? ' bggreenstripes bg-green-600 text-white'
                        : '',
                      j.status == 'COMPLETED' ? ' bg-green-600 text-white' : '',
                      j.status == 'CANCELLED' ? 'bg-red-700 text-white' : '',
                      j.status == 'COMMITTED' ? 'bg-blue-700 text-white' : '',
                    ]"
                    class="rounded h-12 w-full text-right text-sm bg-gray-50 outline-none px-2"
                  >
                    <option>ON-CALL</option>
                    <option>FIRM</option>
                    <option>COMMITTED</option>
                    <option>CANCELLED</option>
                    <option>COMPLETED</option>
                  </select>
                  <div v-if="j.clientSmsStatus && !(j.clientSmsStatus === 'FIRM' && j.status === 'FIRM') && !(j.clientSmsStatus === 'CANCELLED' && j.status === 'CANCELLED')" class="flex items-center mt-1 text-xs">
                    <i v-if="j.clientSmsStatus === 'FIRM'"
                       class="fa-solid fa-circle-check text-green-500 mr-1"></i>
                    <i v-if="j.clientSmsStatus === 'CHANGED'"
                       class="fa-solid fa-circle-question text-yellow-500 mr-1 cursor-pointer" @click="openChangeResolveModal(j)" title="Click to resolve"></i>
                    <i v-if="j.clientSmsStatus === 'CANCELLED'"
                       class="fa-solid fa-circle-xmark text-red-500 mr-1"></i>
                    <span class="text-gray-500">{{ j.clientSmsStatus }} via SMS</span>
                  </div>
                </div>

                <div class="w-full mt-2" @click="duplicateJob(j)">
                  <div
                    class="rounded bg-gray-500 text-white rounded justify-center flex items-center cursor-pointer hover:bg-gray-800 h-14"
                  >
                    Duplicate
                  </div>
                </div>
                <div class="w-full mt-2 relative">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-600"
                  >
                    Tester Response
                  </div>
                  <select @change="updateTesterResponse(j)" v-model="j.testerResponse"
                    :class="{
                      'bg-green-50 text-green-700': j.testerResponse === 'accepted',
                      'bg-red-50 text-red-700': j.testerResponse === 'rejected',
                      'bg-gray-50 text-gray-800': j.testerResponse === 'pending',
                    }"
                    class="rounded h-12 w-full text-right text-sm outline-none px-2">
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div class="w-full mt-2">
                  <div @click="openSmsModal(j)"
                       class="rounded bg-indigo-600 text-white flex items-center justify-center cursor-pointer hover:bg-indigo-800 h-12 text-sm">
                    <i class="fa-solid fa-comment-sms mr-2"></i> Send SMS
                  </div>
                </div>
                <div class="w-full mt-2">
                  <div @click="openLinkModal(j)"
                       class="rounded bg-green-600 text-white flex items-center justify-center cursor-pointer hover:bg-green-800 h-12 text-sm">
                    <i class="fa-solid fa-link mr-2"></i> Client Link
                  </div>
                </div>
              </div>
            </div>
            <div
              class="col-span-4 sm:col-span-1 flex items-center"
              @click="addJob()"
            >
              <div
                class="w-full h-96 flex items-center rounded-lg hover:text-white justify-center bg-gray-100 border-2 border-dashed border-gray-400 cursor-pointer hover:border-gray-600 hover:bg-gray-500 rounded p-4 shadow"
              >
                <div class="w-full text-center">
                  <i class="fa-solid fa-plus text-4xl"></i>
                  <div>Add New Job</div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="border-t border-dashed border-gray-300 pt-10 mt-10 grid grid-cols-4 gap-4"
          >
            <div class="col-span-4 text-left">
              <h2 class="text-lg header-bold text-gray-700">Completed Jobs</h2>
            </div>
            <div
              class="col-span-4 sm:col-span-1"
              v-for="(j, index) in completeJobs"
              :key="index"
            >
              <div
                :class="[
                  editMode == j.id ? 'pulse-glow' : '',
                  j.status == 'COMPLETED'
                    ? 'bggreenstripes bg-green-600'
                    : ' bg-gray-100',
                ]"
                class="w-full rounded p-4 shadow relative"
              >
                <div
                  class="absolute top-0 right-0 -m-1"
                  v-if="index >= 1 || j.id"
                  @click="removeJob(index)"
                >
                  <i
                    class="fa-solid fa-circle-minus text-red-600 hover:text-red-700 cursor-pointer text-lg"
                  ></i>
                </div>
                <div class="w-full relative mb-1">
                  <div class="absolute top-0 right-0 mr-4 mt-5">
                    <i class="fa-solid fa-calendar-days"></i>
                  </div>

                  <flat-pickr
                    class="h-14 cursor-pointer w-full text-xs text-gray-600 text-center bg-gray-200"
                    :config="config"
                    v-model="j.date"
                  ></flat-pickr>
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Job Contact
                  </div>
                  <input
                    type="text"
                    v-model="j.jobContact"
                    placeholder="Job-specific contact"
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Job Phone
                  </div>
                  <input
                    type="text"
                    v-model="j.jobPhone"
                    placeholder="Job-specific mobile"
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Job Email
                  </div>
                  <input
                    type="text"
                    v-model="j.jobEmail"
                    placeholder="Job-specific email"
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Mix Code
                  </div>
                  <input
                    type="text"
                    v-model="j.mixCode"
                    placeholder="ie: 123"
                    class="rounded h-12 text-right w-full bg-gray-50 outline-none px-6 text-gray-600"
                  />
                </div>
                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Meters
                  </div>
                  <input
                    type="text"
                    v-model="j.meters"
                    placeholder="ie: 4"
                    class="rounded h-12 text-right w-full bg-gray-50 outline-none px-6 text-gray-600"
                  />
                </div>
                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Duration (min)
                  </div>
                  <input
                    type="number"
                    v-model="j.duration"
                    placeholder="ie: 60"
                    class="rounded h-12 text-right w-full bg-gray-50 outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Requirements
                  </div>
                  <input
                    type="text"
                    v-model="j.requirements"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>
                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Pour Location
                  </div>
                  <input
                    type="text"
                    v-model="j.pourLocation"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>
                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Notes For Tester
                  </div>
                  <input
                    type="text"
                    v-model="j.notesForTester"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Notes
                  </div>
                  <input
                    type="text"
                    v-model="j.notes"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  />
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-gray-500"
                  >
                    Tester
                  </div>
                  <select
                    type="text"
                    v-model="j.contractor"
                    placeholder="Leave comment..."
                    class="rounded h-12 text-right w-full bg-gray-50 text-xs outline-none px-6 text-gray-600"
                  >
                    <option
                      v-for="(c, cIndex) in contractors"
                      :key="'con' + cIndex"
                      :value="c"
                    >
                      {{ c.name }}
                    </option>
                  </select>
                </div>

                <div class="w-full relative mb-1">
                  <div
                    class="absolute top-0 left-0 ml-2 mt-1 text-xs text-white"
                  >
                    Status
                  </div>
                  <select
                    v-model="j.status"
                    :class="[
                      j.status == 'ON-CALL' ? 'bg-yellow-600 text-white' : '',
                      j.status == 'FIRM'
                        ? ' bggreenstripes bg-green-600 text-white'
                        : '',
                      j.status == 'COMPLETED' ? ' bg-green-600 text-white' : '',
                      j.status == 'CANCELLED' ? 'bg-red-700 text-white' : '',
                      j.status == 'COMMITTED' ? 'bg-blue-700 text-white' : '',
                    ]"
                    class="rounded h-12 w-full text-right text-sm bg-gray-50 outline-none px-2"
                  >
                    <option>ON-CALL</option>
                    <option>FIRM</option>
                    <option>COMMITTED</option>
                    <option>CANCELLED</option>
                    <option>COMPLETED</option>
                  </select>
                </div>

                <div class="w-full mt-2" @click="duplicateJob(j)">
                  <div
                    class="rounded bg-gray-500 text-white rounded justify-center flex items-center cursor-pointer hover:bg-gray-800 h-14"
                  >
                    Duplicate
                  </div>
                </div>
              </div>
            </div>
            <div
              class="col-span-4 sm:col-span-1 flex items-center"
              @click="addJob()"
            >
              <div
                class="w-full h-96 flex items-center rounded-lg hover:text-white justify-center bg-gray-100 border-2 border-dashed border-gray-400 cursor-pointer hover:border-gray-600 hover:bg-gray-500 rounded p-4 shadow"
              >
                <div class="w-full text-center">
                  <i class="fa-solid fa-plus text-4xl"></i>
                  <div>Add New Job</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Map Modal -->
    <div v-if="showMapModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-gray-900 bg-opacity-60 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 flex flex-col" style="height:80vh">
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 class="text-lg font-bold text-gray-900"><i class="fa-solid fa-map-location-dot text-indigo-600 mr-2"></i>Job Locations</h3>
          <button @click="closeMapModal()" class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="flex-1 relative">
          <div id="jobs-schedule-map" class="w-full h-full"></div>
        </div>
      </div>
    </div>

    <!-- Change Resolve Modal -->
    <div v-if="changeResolveModal" class="w-full z-50 h-screen fixed top-0 left-0 bg-secondary bg-opacity-50 flex items-center justify-center" style="backdrop-filter:blur(2px)">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h3 class="text-lg font-bold text-gray-900 mb-4">Resolve Change Request</h3>
        <div v-if="changeResolveJob && changeResolveJob.clientSmsMessage" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <p class="text-xs text-gray-500 mb-1">Client message</p>
          <p class="text-sm text-gray-800 italic">"{{ changeResolveJob.clientSmsMessage }}"</p>
        </div>
        <textarea v-model="changeResolveNote" rows="3" class="w-full border border-gray-300 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-primary mb-4" placeholder="Resolution note (optional)"></textarea>
        <div class="flex space-x-3">
          <button @click="changeResolveModal = false; changeResolveJob = null; changeResolveNote = ''" class="flex-1 h-10 rounded-lg border border-gray-300 text-gray-600 font-medium hover:bg-gray-50">Cancel</button>
          <button @click="resolveChangeRequest()" class="flex-1 h-10 rounded-lg bg-primary text-white font-medium hover:opacity-90">Mark as Resolved</button>
        </div>
      </div>
    </div>
  </div>
</template>

  <script>
  import DropdownSearch from "@/components/DropdownSearch.vue";

import NavMenu from "@/components/headers/navMenu";
import CreateCustomer from "@/components/cards/createCustomer.vue";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";

import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

import { bus } from "../main";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import {
  
  putFire,
  postFire,
  getContractors,
  getJobsByProjectId,
  getFire,
  deleteFire,
  getReminders,
  getProjects,
  getLatestJob,
  getClients,
  setFire
} from "../firestore/utils";
import { getTimeStamp, setGeoPoint } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { writeBatch, doc } from "firebase/firestore";

import { auth, db } from "../firebase";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = "pk.eyJ1IjoicHJlbWFya2V0aG9tZXMiLCJhIjoiY21qamRkb3BsMGU2NzNlb3k4MWs2Y3NtaCJ9.JxBalubMXzSxJWsc2eTWrw";

const geofire = require("geofire-common");
import Multiselect from "vue-multiselect";
import {
  applyCustomFieldTokens,
  loadDefinitions,
  templatesForJob,
} from "../services/customFields";

export default {
  data() {
    return {
      config: {
        enableTime: true,
        altFormat: "d-m-Y H:i",
        altInput: true,
        allowInput: false,
      },
      configDate: {
        enableTime: false,
        altFormat: "d-m-Y",
        altInput: true,
        allowInput: false,
      },
      editedAddress: false,
      addressFound: false,
      showNewClient: false,
      newClient: "",
      completeJobs: [],
      completedJobs: [],
      latestJob: null,
      reminders: [],
      reminderDate: new Date(),
      reminderText: "",
      setReminder: false,
      overrideAddress: "",
      addressObj: null,
      newProject: false,
      // on created
      editJob: null,
      date: null,
      prevDate: null,
      job: null,
      // others
      loadingSelect: false,
      contractors: [],
      editMode: false,
      projects: [],
      autocompleteOptions: {
        types: ["geocode"], // Set the desired place types
      },
      active: true,
      customers: [],
      customer: null,
      callFrequency: "never",
      createCustomer: false,
      client: "",
      clients: [""],
      company: "",
      email: "",
      phone: "",
      secondaryContact: "",
      secondaryPhone: "",
      secondaryEmail: "",
      contractor: null,
      customerId: "",
      address: "",
      region: "",
      projectType: "",
      estimatedStartDate: "",
      estimatedEndDate: "",
      projectId: "",
      projectName: "",
      contact: "",
      poNumber: "",
      jobs: [],
      changeResolveModal: false,
      changeResolveJob: null,
      changeResolveNote: '',
      geoHash: null,
      geoHashPoint: null,
      schema: {
        status: "ON-CALL",
        phone: "",
        email: "",
        contractor: null,
        date: "",
        meters: "",
        duration: "",
        flexible: false,
        rejected: false,
        accepted: false,
        testerResponse: 'pending',
        requirements: "",
        pourLocation: "",
        notesForTester: "",
        notes: "",
        mixCode: "",
        address: "",
        jobContact: "",
        jobPhone: "",
        jobEmail: "",
      },
      smsModal: false,
      smsJob: null,
      smsPhone: '',
      smsMessage: '',
      smsHistory: [],
      smsLoading: false,
      smsHistoryLoading: false,
      smsFilter: 'all',
      smsSelectedJobId: '',
      smsTemplates: [],
      selectedSmsTemplateId: '',
      customFieldDefs: null,
      linkModal: false,
      linkJob: null,
      linkUrl: '',
      linkCopied: false,
      showMapModal: false,
      jobsMap: null,
      jobsMapMarkers: [],
      showNewViewBanner: localStorage.getItem('jobsViewMode') !== 'legacy-dismiss',
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    // Service-locked templates only appear for a job of that service.
    visibleSmsTemplates() {
      return templatesForJob(this.customFieldDefs, this.smsTemplates, this.smsJob);
    },
    smsJobOptions() {
      const allJobs = [...this.jobs, ...this.completeJobs];
      const seen = new Set();
      return allJobs
        .filter(j => {
          if (!j.id || seen.has(j.id)) return false;
          seen.add(j.id);
          return true;
        })
        .sort((a, b) => {
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          return db - da;
        })
        .map(j => ({
          id: j.id,
          label: (j.date ? this.$moment(j.date).format('DD/MM/YYYY HH:mm') : 'No date') +
                 ' — ' + (j.formattedAddress || j.address || 'No address') +
                 (j.status ? ' [' + j.status + ']' : ''),
        }));
    },
  },
  watch: {
    smsFilter(newVal) {
      if (!this.smsModal) return;
      this.smsHistory = [];
      if (newVal === 'all') {
        this.smsSelectedJobId = '';
        this.smsJob = null;
        this.loadAllSmsHistory();
      }
    },
    address(newVal, oldVal){
      console.log("address", newVal)
    },
    projectId(newVal, oldVal) {
      this.getReminders();
      if(!this.newProject) this.getProjectById(true);
    },
    jobs: {
      handler(newVal, oldVal) {
        // Perform any necessary actions
      },
      deep: true, // Enable deep watching
    },
    newProject() {
      if (this.newProject) {
        this.contact = "";
        this.secondaryContact = "";
        this.secondaryPhone = "";
        this.secondaryEmail = "";
        this.poNumber = "";
        this.region = "";
        this.projectType = "";
        this.estimatedStartDate = "";
        this.estimatedEndDate = "";
        this.phone = "";
        this.email = "";
        this.jobs = [this.schema];
        this.address = "";
        this.geoHash = "";
        this.addressObj = null;
        this.geoPoint = null;
        this.editMode = null;
      } else {
        this.selectProject();
      }
    },
    customer() {
      if (!this.customer) {
        this.jobs = [this.schema];
      }
      this.newProject = false;
      
      this.getProjectById();
    },
  },
  methods: {
    openChangeResolveModal(job) {
      this.changeResolveJob = job;
      this.changeResolveNote = '';
      this.changeResolveModal = true;
    },
    async resolveChangeRequest() {
      if (!this.changeResolveJob) return;
      await setFire({ collection: "jobs", doc: this.changeResolveJob.id, body: { clientSmsStatus: null } });
      this.changeResolveModal = false;
      this.changeResolveJob = null;
      this.changeResolveNote = '';
    },
    customLabel (project) {
      return `${project.projectId} – ${project.projectName}`
    },

    showProject(){
      console.log("this.latestJob from show", this.latestJob)
      this.projectId = this.latestJob.projectId + 1;
      this.projectName = '';
      this.newProject = true;      
    },
    openNewClient() {
      if (this.client == "new") this.showNewClient = true;
    },
    async saveClient() {
      // Add to array
      this.clients.push(this.newClient);
      // Select as client
      this.client = this.newClient;

      await postFire({
        collection: "clients",
        body: {
          name: this.client,
          created: new Date(),
        },
      });

      this.showNewClient = false;
    },
    async getReminders() {
      this.reminders = await getReminders({ projectId: this.projectId });
    },
    async getClients() {
      this.clients = await getClients();
    },
    async saveReminder() {
      await postFire({
        collection: "reminders",
        body: {
          projectId: this.projectId,
          reminderDate: new Date(this.$moment(this.reminderDate)),
          reminderText: this.reminderText,
          created: new Date(),
        },
      });
      this.getReminders();
      // this.setReminder = false
    },
    async deleteReminder(reminder) {
      console.log("reminder", reminder);
      await deleteFire({
        collection: "reminders",
        doc: reminder,
      });
      this.getReminders();
    },
    autoSave() {
      // this.submitForm(false)
    },
    selectProject() {
      return new Promise((res, rej) => {
        // this.editMode = true;
        this.newProject = false;
        this.getJobs();
        // this.projects = [];
        res();
      });
    },

    async getJobs() {
      this.completedJobs = [];

      let jobs = await getJobsByProjectId(this.projectId);
      if (jobs.length > 0) {
        jobs = jobs.sort((a, b) => a.date - b.date);

        jobs.forEach((job) => {
          if (job.geohash) this.geoHash = job.geohash;
          if (job.geopoint) this.geoPoint = job.geopoint;
          this.projectName = job.projectName;
          this.phone = job.phone;
          this.region = job.region;
          this.projectType = job.projectType;
          this.client = job.client;
          this.email = job.email;
          this.addressObj = job.address;
          this.contact = job.contact;
          this.secondaryContact = job.secondaryContact || "";
          this.secondaryPhone = job.secondaryPhone || "";
          this.secondaryEmail = job.secondaryEmail || "";
          this.poNumber = job.poNumber;
          this.address = job.formattedAddress;

          // Date assignment
          if (job.estimatedEndDate && job.estimatedEndDate.seconds) {
            
            this.estimatedEndDate = new Date(job.estimatedEndDate.seconds * 1000).toISOString();
          } 
        
          // Date assignment
          if (job.estimatedStartDate && job.estimatedStartDate.seconds) {
            this.estimatedStartDate = new Date(job.estimatedStartDate.seconds * 1000).toISOString();
          }
        
          // Date assignment
          if (job.date && job.date.seconds) {
            job.date = new Date(job.date.seconds * 1000).toISOString();
          } else job.date = "";

          // Derive testerResponse from accepted/rejected booleans
          if (job.rejected) job.testerResponse = 'rejected';
          else if (job.accepted) job.testerResponse = 'accepted';
          else job.testerResponse = 'pending';
        });
        // Get compoleted Jobs
        // this.completeJobs = jobs.filter((j) => j.status == "COMPLETED");
        this.completeJobs = jobs.filter((j) => j.status === "COMPLETED" || j.status === "CANCELLED");

        this.completeJobs = this.completeJobs.sort((a, b) => a.date - b.date);
        this.jobs = jobs.sort((a, b) => a.date - b.date);
        // Make active if one exists
        const _getJob = this.jobs.filter(
          (i) => i.id == this.$route.query.jobId
        );
        if (_getJob.length > 0) {
          const jobIndex = this.jobs.indexOf(_getJob[0]);
          if (jobIndex >= 0) {
            this.active = this.jobs[jobIndex].active;
          }
        }
      } else {
        // this.newProject = true
      }
    },

    formatAddress(address) {
      if (address && address.route) {
        const add = `${address.street_number || ""} ${address.route || ""} ${
          address.locality || ""
        }, ${address.administrative_area_level_1 || ""} ${
          address.country || ""
        } ${address.postal_code || ""}`;
        console.log("formatted address without", add);
        return add;
      } else {
        return this.overrideAddress;
      }
    },
    // Address Formatting
    getAddressData(addressData, placeResultData, id) {
      if (addressData && addressData.route && addressData.street_number) {
        this.address = addressData;
        this.addressFound = true;
        console.log("addressData if statment", addressData);
      } else {
        this.address = addressData;
        this.addressFound = false;
      }
    },
    onInput(a) {
      // this.autoSave()
      this.overrideAddress = a.newVal;
      this.editedAddress = true;
      // this.address = this.overrideAddress;
    },
    async updateTesterResponse(job){
      const val = job.testerResponse;
      const body = {
        accepted: val === 'accepted',
        rejected: val === 'rejected',
      };
      if (val === 'rejected') body.rejectedDate = new Date();
      await setFire({
        collection: "jobs",
        doc: job.id,
        body,
      });
    },

    async getCurrentUser() {
        return new Promise((resolve, reject) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            user ? resolve(user) : reject(new Error("No user is signed in."));
          });
        });
      },

    // Saving Functionality

async submitForm(_redirect) {
      if (this.newProject && this.projectId <= this.latestJob.projectId)
        alert(
          `Project Id must be larger than the lastest project which is ${this.latestJob.projectId}`
        );
      if (
        this.projectId &&
        this.projectName &&
        this.contact &&
        this.phone &&
        this.poNumber &&
        this.email
      ) {
        try {
          this.loading = true;
          // GEO Hash
          let geohash = "";
          let geopoint = null;
          let _address = this.addressObj;
          let _formattedAddress = "";

          if (typeof this.address === "string")
            _formattedAddress = this.address;

          // if(_address && _address.route) _formattedAddress = this.formatAddress(_address)

          if (!this.address && !this.address.latitude) {
            _formattedAddress = this.overrideAddress;
            _address = null;
            geohash = "";
            geopoint = null;
          }

          if (this.editedAddress && this.address && this.address.latitude) {
            _formattedAddress = this.formatAddress(this.address);
            const lat = this.address.latitude;
            const lng = this.address.longitude;
            console.log("Address at addreess", this.address);
            console.log("Address at geo", this.address.latitude);
            console.log("Address at geo longitude", this.address.longitude);
            _address = this.address;
            geohash = geofire.geohashForLocation([lat, lng]);
            // GEO point
            geopoint = setGeoPoint({
              latitude: this.address.latitude,
              longitude: this.address.longitude,
            });
          }

          let newArray = [];
          // Loop Jobs and save
          for (let i = 0; i < this.jobs.length; i++) {
            const estStart = new Date(this.estimatedStartDate)
            const estEnd = new Date(this.estimatedEndDate)
            let payload = {
              // Client
              // formattedAddress: _formattedAddress,
              client: this.client,
              active: this.active,
              // Customer
              company: this.customer.company,
              phone: this.phone,
              email: this.email,
              callFrequency: this.callFrequency,
              contact: this.contact,
              secondaryContact: this.secondaryContact,
              secondaryPhone: this.secondaryPhone,
              secondaryEmail: this.secondaryEmail,
              poNumber: this.poNumber,
              region: this.region,
              projectType: this.projectType,
              estimatedStartDate: getTimeStamp(estStart),
              estimatedEndDate: getTimeStamp(estEnd),
              customerId: this.customer.id,
              // Project Specific
              // address: _address,
              projectId: this.projectId,
              projectName: this.projectName,
              geohash,
              geopoint,
              // Job specific
              status: this.jobs[i].status,
              contractor: this.jobs[i].contractor,
              meters: this.jobs[i].meters,
              duration: this.jobs[i].duration || "",
              flexible: this.jobs[i].flexible || false,
              mixCode: this.jobs[i].mixCode,
              accepted: this.jobs[i].accepted,
              rejected: this.jobs[i].rejected,
              requirements: this.jobs[i].requirements,
              pourLocation: this.jobs[i].pourLocation || "",
              notesForTester: this.jobs[i].notesForTester || "",
              notes: this.jobs[i].notes,
              jobContact: this.jobs[i].jobContact || '',
              jobPhone: this.jobs[i].jobPhone || '',
              jobEmail: this.jobs[i].jobEmail || '',
              updated: new Date(),
            };

            let projectPayload = {
              client: this.client,
              active: this.active,
              // Customer
              company: this.customer.company,
              phone: this.phone,
             
              email: this.email,
              callFrequency: this.callFrequency,
              contact: this.contact,
              secondaryContact: this.secondaryContact,
              secondaryPhone: this.secondaryPhone,
              secondaryEmail: this.secondaryEmail,
              poNumber: this.poNumber,
              region: this.region,
              projectType: this.projectType,
              estimatedStartDate: getTimeStamp(estStart),
              estimatedEndDate: getTimeStamp(estEnd),
              customerId: this.customer.id,
              // Project Specific
              // address: _address,
              projectId: this.projectId,
              projectName: this.projectName,
              geohash,
              geopoint,
            }

            if (this.newProject) {
              payload["created"] = new Date();
              projectPayload["created"] = new Date();
              // payload['lastCalled'] = new Date()
            }

            if (this.editedAddress || this.address != "") {
              // alert()
              this.$set(payload, "address", _address);
              this.$set(projectPayload, "address", _address);
              this.$set(payload, "formattedAddress", _formattedAddress);
              this.$set(projectPayload, "formattedAddress", _formattedAddress);
            }

            if (this.jobs[i].published)
              payload["published"] = this.jobs[i].published;

            if (this.jobs[i].date)
              (payload["date"] = getTimeStamp(new Date(this.jobs[i].date)))

              payload['accepted'] =  this.jobs[i].accepted || false;
              payload['rejected'] =  this.jobs[i].rejected || false;

            let a;

            const _user = await this.getCurrentUser();

            if (this.jobs[i].id) {
              // alert(this.jobs[i].id)
              a = await putFire({
                collection: "jobs",
                doc: this.jobs[i].id,
                body: payload,
              });


               await postFire({
              collection: "customerPosts",
            body: {
                    postedBy: _user.uid,
                    type: "jobUpdated",
                    body: `A job has been updated for ${this.projectName} at ${_formattedAddress}}.`,
                    attachments: null,
                    projectId: this.projectId,
                    customerId: this.customer.id,
                    created: new Date(),
                }});
              

            } else {
              a = await postFire({
                collection: "jobs",
                body: payload,
              });

              await postFire({
                collection: "customerPosts",
                body: {
                    postedBy: _user.uid,
                    type: "jobCreated",
                    body: `A job has been updated for ${this.projectName} at ${_formattedAddress}}.`,
                    projectId: this.projectId,
                    customerId: this.customer.id,
                    created: new Date(),
                }
              });
            }

            const p = {
                collection: "projects",
                doc: JSON.stringify(this.projectId),
                body: projectPayload,
              }
            
            try{
            await setFire(p);
            await postFire({
              collection: "customerPosts",
            body: {
                    postedBy: _user.uid,
                    type: "projectUpdated",
                    body: `A project was updated for ${this.projectName} at ${_formattedAddress}}.`,
                    projectId: this.projectId,
                    customerId: this.customer.id,
                    created: new Date(),
            }});

            } catch(e){
              console.log("Failed to save project", e)
            }

            if (a.date && a.date.seconds)
              a.date = new Date(a.date.seconds * 1000).toISOString();
            newArray.push(a);
          }

          if (this.newProject) {
            this.client = this.clients[0];
            this.customer = null;
            this.jobs = [this.schema];
            this.projectName = "";
            this.projectId = this.latestJob.projectId + 1;
            this.status = "";
            this.callFrequency = "";
            this.contact = "";
            this.secondaryContact = "";
            this.secondaryPhone = "";
            this.secondaryEmail = "";
            this.poNumber = "";
            this.region = "",
            this.projectType = "",
            this.estimatedStartDate = "";
            this.estimatedEndDate = "";
            this.phone = "";
            this.email = "";
            this.search = "";
            this.projects = [];
            this.address = "";
            this.$refs.autocomplete.clear();
            this.editMode = null;
          }

          if (_redirect) this.$router.back(-1);
        } catch (e) {
          alert("Something went wrong");
          console.log("Error", e);
        }
      } else {
        alert(
          "Required fields - Project Id, ProjectName, Contact, Phone, Email"
        );
      }
    },



    // Get projects when a client changes
    async getProjectById(override) {
      console.log("this.customer", this.customer);
      const objects = await getProjects({ companyName: this.customer.company });

      const uniqueObjects = objects.filter((obj, index, self) => {
        return (
          index ===
          self.findIndex((otherObj) => otherObj.projectId === obj.projectId)
        );
      });

      console.log("uniqueObjects", uniqueObjects);
      if (uniqueObjects.length > 0) {
        if (!override) this.projectId = uniqueObjects[0].projectId;

        uniqueObjects.sort((a, b) => {
          const nameA = a.projectName.toUpperCase(); // Convert names to uppercase for case-insensitive comparison
          const nameB = b.projectName.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0; // names must be equal
        });

        this.projects = uniqueObjects;
        if (!override && this.$route.query.jobId)
          this.projectId = this.job.projectId;

        this.selectProject();
      }
    },
    // general get data
    searchCustomers() {
      this.loadingSelect = true;
      // this.customers = [];
      const url =
        "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/search";

      this.axios
        .get(url, {
          params: {
            indexName: "customers",
            filter: null,
            search: this.search,
            twilio: null,
          },
        })
        .then((response) => {
          console.log("response from search", response);
          this.customers = response.data;
          this.loadingSelect = false;
        })
        .catch((e) => {
          this.loadingSelect = false;
          console.log("Error algolia", e);
        });
    },

    getCustomer() {
      return new Promise(async (res, rej) => {
        this.customer = await getFire({
          collection: "customers",
          doc: this.job.customerId,
        });
        res();
      });
    },
    getJob() {
      return new Promise(async (res, rej) => {
        this.job = await getFire({
          collection: "jobs",
          doc: this.$route.query.jobId,
        });
        this.contact = this.job.contact;
        this.poNumber = this.job.poNumber;
        this.phone = this.job.phone;
        this.email = this.job.email;
        this.callFrequency = this.job.callFrequency;
        this.projectId = this.job.projectId;
        this.region = this.job.region;
        this.projectType = this.job.projectType;
        if(this.job.estimatedStartDate) this.estimatedStartDate = new Date(this.job.estimatedStartDate.seconds * 1000).toISOString();
        if(this.job.estimatedEndDate) this.estimatedEndDate = new Date(this.job.estimatedEndDate.seconds * 1000).toISOString();
        res();
      });
    },
    async getContractors() {
      this.contractors = await getContractors();
    },
    // CRUD for jobs
    removeJob(index) {
      if (window.confirm("Are you sure you want to delete this job?")) {
        if (this.jobs[index].id) this.deleteJob(this.jobs[index].id);
        let jobs = JSON.stringify(this.jobs);
        jobs = JSON.parse(jobs);
        jobs.splice(index, 1);
        this.jobs = jobs;

        if (this.jobs.length == 0) this.jobs.push(this.schema);
      }
    },
    async deleteJob(jobId) {
      await deleteFire({ collection: "jobs", doc: jobId });
    },
    addJob() {
      const s = JSON.stringify(this.schema);
      this.jobs.push(JSON.parse(s));
    },
    duplicateJob(job) {
      const s = JSON.stringify(job);
      let _job = JSON.parse(s);
      if (_job.id) _job.id = "";

      _job.testerNotes = "";
      _job.published = false;
      _job.contractor = null;
      this.jobs.push(_job);
    },

    async getTheLatestJob() {
      this.latestJob = await getLatestJob();
    
      console.log("this.latestJob", this.latestJob);
      if (!this.latestJob) this.latestJob = { projectId: 0 };
      this.projectId = this.latestJob.projectId + 1;
      console.log("latestJob", this.latestJob);
    },

    loadGoogleMapsScript() {
      // Check if the script is already loaded
      if (!document.querySelector('script[src="https://maps.googleapis.com/maps/api/js"]')) {
        const script = document.createElement('script');
        script.src = "https://maps.googleapis.com/maps/api/js?key=AIzaSyAgzzzBwnKAnilSbETwkny6ApTz18jlHr4&libraries=places";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    },

    normalizePhoneForSms(phone) {
      if (!phone) return '';
      let p = phone.replace(/[\s\-\(\)]/g, '');
      if (p.startsWith('04')) p = '+61' + p.slice(1);
      else if (p.startsWith('614') && !p.startsWith('+')) p = '+' + p;
      else if (p.startsWith('61') && !p.startsWith('+')) p = '+' + p;
      return p;
    },

    async openSmsModal(job) {
      this.smsJob = job;
      this.smsFilter = 'job';
      this.smsSelectedJobId = job.id || '';
      this.smsPhone = this.normalizePhoneForSms(job.phone || this.phone);
      this.smsMessage = '';
      this.smsModal = true;
      this.smsHistory = [];
      this.selectedSmsTemplateId = '';
      this.loadSmsTemplates();
      if (job.id) {
        await this.loadSmsHistoryForJob(job.id);
      }
    },

    async loadSmsTemplates() {
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await fetch(
          'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/settings/sms/templates',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        this.smsTemplates = (json.data || []).filter(t => t.type === 'single' || t.type === 'all');
      } catch (e) {
        console.error('Failed to load SMS templates:', e);
        this.smsTemplates = [];
      }
    },

    applySmsTemplate() {
      if (!this.selectedSmsTemplateId) return;
      const tpl = this.smsTemplates.find(t => t.id === this.selectedSmsTemplateId);
      if (tpl) {
        this.smsMessage = tpl.body;
      }
    },
    buildSmsBody(template, job) {
      const link = job.token ? `${window.location.origin}/job/${job.token}` : '';
      const body = template
        .replace(/\{recipientName\}/g, job.contact || '')
        .replace(/\{receiverName\}/g, job.contact || '')
        .replace(/\{company\}/g, job.company || '')
        .replace(/\{contact\}/g, job.contact || '')
        .replace(/\{address\}/g, job.formattedAddress || job.fullAddress || '')
        .replace(/\{date\}/g, job.date || '')
        .replace(/\{time\}/g, job.time || '')
        .replace(/\{meters\}/g, job.meters || '')
        .replace(/\{project\}/g, job.projectName || '')
        .replace(/\{link\}/g, link);
      return applyCustomFieldTokens(this.customFieldDefs, job, body);
    },

    // ==================== MAP MODAL ====================

    getJobCoords(item) {
      if (!item) return null;
      if (item.address && item.address.latitude && item.address.longitude) {
        return { lat: item.address.latitude, lng: item.address.longitude };
      }
      if (item.geopoint) {
        const gp = item.geopoint;
        if (gp.latitude && gp.longitude) return { lat: gp.latitude, lng: gp.longitude };
        if (gp._lat && gp._long) return { lat: gp._lat, lng: gp._long };
      }
      if (item.latitude && item.longitude) return { lat: item.latitude, lng: item.longitude };
      return null;
    },

    openMapModal() {
      this.showMapModal = true;
      this.$nextTick(() => this.initJobsMap());
    },

    closeMapModal() {
      this.showMapModal = false;
      this.destroyJobsMap();
    },

    initJobsMap() {
      const container = document.getElementById('jobs-schedule-map');
      if (!container) return;

      this.destroyJobsMap();

      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.jobsMap = new mapboxgl.Map({
        container: 'jobs-schedule-map',
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [151.2093, -33.8688],
        zoom: 10,
      });
      this.jobsMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

      this.jobsMap.on('load', () => {
        // Only show visible (non-completed, non-cancelled) jobs — same filter as the table
        const visibleJobs = this.jobs.filter(j => j.status !== 'COMPLETED' && j.status !== 'CANCELLED');
        const bounds = new mapboxgl.LngLatBounds();
        let hasPoints = false;

        visibleJobs.forEach((job) => {
          const coords = this.getJobCoords(job);
          if (!coords) return;

          bounds.extend([coords.lng, coords.lat]);
          hasPoints = true;

          const time = job.date ? this.$moment(job.date).format('HH:mm') : '';
          const dateStr = job.date ? this.$moment(job.date).format('DD/MM') : '';
          const address = job.formattedAddress || '';
          const status = job.status || '';
          const tester = job.contractor && job.contractor.name ? job.contractor.name : 'Unassigned';
          const mix = job.mixCode ? `${job.mixCode}` : '';
          const meters = job.meters ? `${job.meters}m³` : '';

          // Marker element
          const el = document.createElement('div');
          el.style.cssText = 'width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:3px solid #6366f1;background:#1a1a2e;color:#6366f1;cursor:pointer;box-shadow:0 0 10px #6366f188;';
          el.textContent = time || '?';

          const popupHtml = `<div style="font-size:12px;padding:6px;min-width:160px;">
            <div style="font-weight:bold;margin-bottom:4px;">${time} ${dateStr}</div>
            <div style="color:#666;margin-bottom:2px;">${address}</div>
            <div style="margin-bottom:2px;"><span style="font-weight:600;">Status:</span> ${status}</div>
            <div style="margin-bottom:2px;"><span style="font-weight:600;">Tester:</span> ${tester}</div>
            ${mix || meters ? `<div>${[mix, meters].filter(Boolean).join(' — ')}</div>` : ''}
          </div>`;

          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat([coords.lng, coords.lat])
            .setPopup(new mapboxgl.Popup({ offset: 10 }).setHTML(popupHtml))
            .addTo(this.jobsMap);
          this.jobsMapMarkers.push(marker);
        });

        if (hasPoints) {
          if (this.jobsMapMarkers.length === 1) {
            const lngLat = this.jobsMapMarkers[0].getLngLat();
            this.jobsMap.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: 14 });
          } else {
            this.jobsMap.fitBounds(bounds, { padding: 60, maxZoom: 15 });
          }
        }
      });
    },

    destroyJobsMap() {
      this.jobsMapMarkers.forEach(m => m.remove());
      this.jobsMapMarkers = [];
      if (this.jobsMap) {
        this.jobsMap.remove();
        this.jobsMap = null;
      }
    },

    async openSmsInbox() {
      this.smsFilter = 'all';
      this.smsSelectedJobId = '';
      this.smsJob = null;
      this.smsPhone = this.normalizePhoneForSms(this.phone);
      this.smsMessage = '';
      this.smsModal = true;
      this.smsHistory = [];
      await this.loadAllSmsHistory();
    },

    async onSmsJobFilterChange() {
      if (!this.smsSelectedJobId) {
        this.smsHistory = [];
        this.smsJob = null;
        return;
      }
      const allJobs = [...this.jobs, ...this.completeJobs];
      this.smsJob = allJobs.find(j => j.id === this.smsSelectedJobId) || null;
      await this.loadSmsHistoryForJob(this.smsSelectedJobId);
    },

    async loadSmsHistoryForJob(jobId) {
      this.smsHistoryLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const res = await this.axios.get(
          `https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages/job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const msgs = res.data.data || res.data || [];
        this.smsHistory = this._normalizeSmsMessages(msgs);
      } catch (e) {
        console.error('Failed to load SMS history:', e);
        this.smsHistory = [];
      } finally {
        this.smsHistoryLoading = false;
      }
    },

    async loadAllSmsHistory() {
      this.smsHistoryLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const baseUrl = 'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages';

        // Build a job lookup map for labelling
        const allJobs = [...this.jobs, ...this.completeJobs];
        const jobMap = {};
        allJobs.forEach(j => {
          if (j.id) {
            jobMap[j.id] = j.date
              ? this.$moment(j.date).format('DD/MM/YY') + ' — ' + (j.formattedAddress || '').split(',')[0]
              : (j.formattedAddress || '').split(',')[0] || j.id;
          }
        });

        // Fetch messages for each job in project
        const jobFetches = allJobs.filter(j => j.id).map(j =>
          this.axios.get(`${baseUrl}/job/${j.id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
              const msgs = res.data.data || res.data || [];
              return msgs.map(m => ({ ...m, _jobLabel: jobMap[j.id] || j.id, jobId: j.id }));
            })
            .catch(() => [])
        );

        // Fetch general messages by phone (primary + secondary)
        const phoneFetches = [];
        const phones = new Set();
        if (this.phone) phones.add(this.normalizePhoneForSms(this.phone));
        if (this.secondaryPhone) phones.add(this.normalizePhoneForSms(this.secondaryPhone));
        for (const ph of phones) {
          if (ph) {
            phoneFetches.push(
              this.axios.get(`${baseUrl}?to=${encodeURIComponent(ph)}`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => {
                  const msgs = res.data.data || res.data || [];
                  return msgs.map(m => ({ ...m, _source: 'general' }));
                })
                .catch(() => [])
            );
          }
        }

        const results = await Promise.all([...jobFetches, ...phoneFetches]);
        let allMsgs = results.flat();

        // De-duplicate by messageId or by body+timestamp
        const seen = new Set();
        allMsgs = allMsgs.filter(m => {
          const key = m.messageId || m.id || (m.body || m.message || '') + '|' + this._getTimestampMs(m.timestamp || m.created);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        // Sort newest first
        allMsgs.sort((a, b) => {
          return this._getTimestampMs(b.timestamp || b.created) - this._getTimestampMs(a.timestamp || a.created);
        });

        this.smsHistory = this._normalizeSmsMessages(allMsgs);
      } catch (e) {
        console.error('Failed to load all SMS history:', e);
        this.smsHistory = [];
      } finally {
        this.smsHistoryLoading = false;
      }
    },

    _normalizeSmsMessages(msgs) {
      return msgs.map(m => ({
        ...m,
        direction: m.direction || (m.sent !== undefined ? 'outbound' : 'outbound'),
        body: m.body || m.message || '',
        timestamp: m.timestamp || m.created || null,
      }));
    },

    _getTimestampMs(ts) {
      if (!ts) return 0;
      if (ts.seconds) return ts.seconds * 1000;
      if (ts._seconds) return ts._seconds * 1000;
      const d = new Date(ts).getTime();
      return isNaN(d) ? 0 : d;
    },

    async sendJobSms() {
      if (!this.smsPhone || !this.smsMessage) return;
      this.smsLoading = true;
      try {
        const user = await this.getCurrentUser();
        const token = await user.getIdToken();
        const baseUrl = 'https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/messages';

        const message = this.smsJob ? this.buildSmsBody(this.smsMessage, this.smsJob) : this.smsMessage;
        if (this.smsJob && this.smsJob.id) {
          // Send linked to a specific job
          await this.axios.post(
            `${baseUrl}/job/${this.smsJob.id}`,
            { message, to: this.smsPhone },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else {
          // Send general (not linked to a specific job)
          await this.axios.post(
            baseUrl,
            { message, to: this.smsPhone },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }

        this.smsMessage = '';

        // Refresh the view
        if (this.smsFilter === 'all') {
          await this.loadAllSmsHistory();
        } else if (this.smsSelectedJobId) {
          await this.loadSmsHistoryForJob(this.smsSelectedJobId);
        }
      } catch (e) {
        console.error('Failed to send SMS:', e);
        alert('Failed to send SMS. Please try again.');
      } finally {
        this.smsLoading = false;
      }
    },

    formatSmsTimestamp(ts) {
      if (!ts) return '';
      let ms;
      if (ts.seconds) ms = ts.seconds * 1000;
      else if (ts._seconds) ms = ts._seconds * 1000;
      else ms = new Date(ts).getTime();
      if (isNaN(ms)) return '';
      const m = this.$moment(ms);
      if (m.isSame(this.$moment(), 'day')) return m.format('h:mm A');
      if (m.isSame(this.$moment(), 'year')) return m.format('DD MMM, h:mm A');
      return m.format('DD/MM/YYYY h:mm A');
    },

    async openLinkModal(job) {
      this.linkJob = job;
      this.linkCopied = false;
      const secret = 'kct-client-link-s3cret-2024';
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        'raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
      );
      const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(job.id));
      const hex = Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('');
      const token = `${job.id}~${hex.slice(0, 16)}`;
      this.linkUrl = `${window.location.origin}/job/${token}`;
      this.linkModal = true;
    },

    tryNewView() {
      localStorage.removeItem('jobsViewMode');
      this.$router.push({ name: 'Jobs' });
    },

    dismissNewViewBanner() {
      this.showNewViewBanner = false;
      localStorage.setItem('jobsViewMode', 'legacy-dismiss');
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.linkUrl);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      } catch (e) {
        const input = document.createElement('input');
        input.value = this.linkUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2000);
      }
    },

  },
  
  components: {
    DropdownSearch,
    datetime: Datetime,
    VueGoogleAutocomplete,
    CreateCustomer,
    Multiselect,
    NavMenu,
    flatPickr,
  },
  mounted(){
    this.loadGoogleMapsScript();
  },
  async created() {
    // props: ["editJob", "date", "prevDate"],
    loadDefinitions().then((defs) => { this.customFieldDefs = defs; });
    this.getTheLatestJob();
    if (this.$route.query.jobId) {
      this.editMode = this.$route.query.jobId;
      await this.getJob();
      await this.getCustomer();
      await this.selectProject();
    } else {
      this.newProject = true;
      const s = JSON.stringify(this.schema);
      this.jobs.push(JSON.parse(s));
    }

    // Load contractors for teser field on each job
    this.getClients();
    this.getContractors();

    // Listeners
    bus.$on("closeCreateCustomer", (data) => {
      this.createCustomer = data.status;
      if (data.customer) {
        this.customer = data.customer;
      }
    });
  },
};
</script>
  
  <style lang="postcss">
@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 128, 128, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(0, 128, 128, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 128, 128, 0);
  }
}

.pulse-glow {
  animation: pulse-glow 2s infinite;
}

.theme-orange .vdatetime-popup__header,
.theme-orange .vdatetime-calendar__month__day--selected > span > span,
.theme-orange .vdatetime-calendar__month__day--selected:hover > span > span {
  background: #01bc38 !important;
}

.theme-orange .vdatetime-year-picker__item--selected,
.theme-orange .vdatetime-time-picker__item--selected,
.theme-orange .vdatetime-popup__actions__button {
  color: #01bc38 !important;
}

/* iMessage-style chat bubbles */
.imsg-bubble {
  border-radius: 18px;
  word-break: break-word;
  line-height: 1.35;
  position: relative;
}
.imsg-bubble-sent {
  background: #007aff;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.imsg-bubble-recv {
  background: #e5e5ea;
  color: #000;
  border-bottom-left-radius: 4px;
}
</style>
  